import { Fragment } from 'react';
import { Disclosure, Menu, Transition } from '@headlessui/react';
import { Bars3Icon, XMarkIcon, SunIcon, MoonIcon } from '@heroicons/react/24/outline';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.js';
import { useTheme } from '../contexts/ThemeContext.js';

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

export const Layout = ({ children }: { children: React.ReactNode }) => {
  const { user, logout, isAuthenticated } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const isDark = theme === 'dark';

  // Show loading state while checking auth
  if (isAuthenticated === null) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  // If not authenticated, just render children without layout
  if (!isAuthenticated || !user) {
    return <div className={isDark ? 'dark' : ''}>{children}</div>;
  }

  const navigation = [
    { 
      name: 'Dashboard', 
      href: user?.role === 'ADMIN' ? '/admin/dashboard' : '/dashboard', 
      current: user?.role === 'ADMIN' 
        ? location.pathname.startsWith('/admin') 
        : location.pathname === '/dashboard' 
    },
    { 
      name: 'Chat', 
      href: '/chat', 
      current: location.pathname === '/chat' 
    },
    { 
      name: 'Admin', 
      href: '/admin', 
      current: location.pathname.startsWith('/admin'), 
      adminOnly: true 
    },
  ].filter(item => !item.adminOnly || user?.role === 'ADMIN');

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className={`min-h-screen ${isDark ? 'dark bg-gray-900' : 'bg-gray-50'}`}>
      <div className="min-h-full transition-colors duration-200">
        <Disclosure as="nav" className={`${isDark ? 'bg-gray-800' : 'bg-white'} shadow-sm transition-colors duration-200`}>
          {({ open }) => (
            <>
              <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <div className="flex h-16 justify-between">
                  <div className="flex items-center space-x-4">
                    <button
                      onClick={toggleTheme}
                      className="p-2 rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      aria-label={`Switch to ${isDark ? 'light' : 'dark'} mode`}
                    >
                      {isDark ? (
                        <SunIcon className="h-5 w-5 text-yellow-400" />
                      ) : (
                        <MoonIcon className="h-5 w-5 text-gray-700" />
                      )}
                    </button>
                    <div className="flex-shrink-0 items-center">
                      <span className={`text-xl font-bold ${isDark ? 'text-white' : 'text-indigo-600'}`}>
                        ProcEngAI
                      </span>
                    </div>
                    <div className="hidden sm:ml-6 sm:flex sm:space-x-2">
                      <div className="flex space-x-2">
                        <Link
                          to={user.role === 'ADMIN' ? '/admin/dashboard' : '/dashboard'}
                          className={`px-3 py-2 rounded-md text-sm font-medium ${
                            (user.role === 'ADMIN' 
                              ? location.pathname.startsWith('/admin')
                              : location.pathname === '/dashboard')
                              ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900 dark:text-white'
                              : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white'
                          }`}
                        >
                          Dashboard
                        </Link>
                        <Link
                          to="/chat"
                          className={`px-3 py-2 rounded-md text-sm font-medium ${
                            location.pathname === '/chat'
                              ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900 dark:text-white'
                              : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white'
                          }`}
                        >
                          Chat
                        </Link>
                      </div>
                      <Menu as="div" className="relative ml-3">
                        <div>
                          <Menu.Button className={`flex rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                            isDark ? 'bg-gray-700' : 'bg-white'
                          }`}>
                            <span className="sr-only">Open user menu</span>
                            <img
                              className="h-8 w-8 rounded-full"
                              src={`https://ui-avatars.com/api/?name=${encodeURIComponent(user.name || user.email)}&background=4f46e5&color=fff`}
                              alt={user.name || user.email}
                            />
                          </Menu.Button>
                        </div>
                        <Transition
                          as={Fragment}
                          enter="transition ease-out duration-200"
                          enterFrom="transform opacity-0 scale-95"
                          enterTo="transform opacity-100 scale-100"
                          leave="transition ease-in duration-75"
                          leaveFrom="transform opacity-100 scale-100"
                          leaveTo="transform opacity-0 scale-95"
                        >
                          <Menu.Items className={`absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none transition-all duration-200 ${
                            isDark ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'
                          }`}>
                            <Menu.Item>
                              {({ active }) => (
                                <Link
                                  to="/profile"
                                  className={classNames(
                                    active ? (isDark ? 'bg-gray-700' : 'bg-gray-100') : '',
                                    'block px-4 py-2 text-sm transition-colors duration-150',
                                    isDark ? 'text-gray-200 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'
                                  )}
                                >
                                  Your Profile
                                </Link>
                              )}
                            </Menu.Item>
                            <Menu.Item>
                              {({ active }) => (
                                <button
                                  onClick={handleLogout}
                                  className={classNames(
                                    active ? (isDark ? 'bg-gray-700' : 'bg-gray-100') : '',
                                    'block w-full px-4 py-2 text-left text-sm transition-colors duration-150',
                                    isDark ? 'text-gray-200 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'
                                  )}
                                >
                                  Sign out
                                </button>
                              )}
                            </Menu.Item>
                          </Menu.Items>
                        </Transition>
                      </Menu>
                    </div>
                  </div>
                  <div className="-mr-2 flex items-center sm:hidden">
                    <Disclosure.Button className="inline-flex items-center justify-center rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
                      <span className="sr-only">Open main menu</span>
                      {open ? (
                        <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
                      ) : (
                        <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
                      )}
                    </Disclosure.Button>
                  </div>
                </div>
              </div>

              <Disclosure.Panel className="sm:hidden">
                <div className="space-y-1 pb-3 pt-2">
                  <Disclosure.Button
                    as={Link}
                    to={user.role === 'ADMIN' ? '/admin/dashboard' : '/dashboard'}
                    className={classNames(
                      (user.role === 'ADMIN' 
                        ? location.pathname.startsWith('/admin')
                        : location.pathname === '/dashboard')
                        ? 'bg-indigo-50 border-indigo-500 text-indigo-700 dark:bg-indigo-900 dark:text-white'
                        : 'border-transparent text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white',
                      'block pl-3 pr-4 py-2 border-l-4 text-base font-medium w-full text-left'
                    )}
                  >
                    Dashboard
                  </Disclosure.Button>
                  <Disclosure.Button
                    as={Link}
                    to="/chat"
                    className={classNames(
                      location.pathname === '/chat'
                        ? 'bg-indigo-50 border-indigo-500 text-indigo-700 dark:bg-indigo-900 dark:text-white'
                        : 'border-transparent text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white',
                      'block pl-3 pr-4 py-2 border-l-4 text-base font-medium w-full text-left'
                    )}
                  >
                    Chat
                  </Disclosure.Button>
                </div>
                <div className={`border-t ${isDark ? 'border-gray-700' : 'border-gray-200'} pb-3 pt-4`}>
                  <div className="flex items-center px-4">
                    <div className="flex-shrink-0">
                      <img
                        className="h-10 w-10 rounded-full"
                        src={`https://ui-avatars.com/api/?name=${encodeURIComponent(user.name || user.email)}&background=4f46e5&color=fff`}
                        alt={user.name || user.email}
                      />
                    </div>
                    <div className="ml-3">
                      <div className={`text-base font-medium ${
                        isDark ? 'text-white' : 'text-gray-800'
                      }`}>
                        {user.name}
                      </div>
                      <div className={`text-sm font-medium ${
                        isDark ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        {user.email}
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 space-y-1">
                    <Link
                      to="/profile"
                      className={`block px-4 py-2 text-base font-medium ${
                        isDark
                          ? 'text-gray-300 hover:bg-gray-700 hover:text-white'
                          : 'text-gray-500 hover:bg-gray-100 hover:text-gray-800'
                      }`}
                    >
                      Your Profile
                    </Link>
                    <button
                      onClick={handleLogout}
                      className={`block w-full px-4 py-2 text-left text-base font-medium ${
                        isDark
                          ? 'text-gray-300 hover:bg-gray-700 hover:text-white'
                          : 'text-gray-500 hover:bg-gray-100 hover:text-gray-800'
                      }`}
                    >
                      Sign out
                    </button>
                  </div>
                </div>
              </Disclosure.Panel>
            </>
          )}
        </Disclosure>

        <main className="transition-colors duration-200">
          <div className={`mx-auto max-w-7xl py-6 sm:px-6 lg:px-8 ${
            isDark ? 'text-gray-100' : 'text-gray-900'
          }`}>
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};
